import type { FacebookSetupInfo } from "@/modules/facebook/presentation/api/types";
import { Alert, AlertDescription, AlertTitle, Button } from "@devhop/ui";
import { AlertTriangle, Copy } from "lucide-react";

type FacebookDevSetupAlertProps = {
  setup: FacebookSetupInfo | undefined;
};

export function FacebookDevSetupAlert({ setup }: FacebookDevSetupAlertProps) {
  if (!setup) return null;

  const show =
    !setup.configured ||
    setup.usesLocalhostRedirect ||
    setup.hints.length > 0;

  if (!show) return null;

  const copyRedirectUri = async () => {
    await navigator.clipboard.writeText(setup.oauthRedirectUri);
  };

  return (
    <Alert variant="destructive" className="mb-6 border-amber-500/50 bg-amber-500/5">
      <AlertTriangle className="size-4 text-amber-600" />
      <AlertTitle className="text-amber-900 dark:text-amber-100">
        ການຕັ້ງຄ່າ Facebook ສຳລັບ local / dev
      </AlertTitle>
      <AlertDescription className="space-y-3 text-amber-950/90 dark:text-amber-50/90">
        {!setup.configured ? (
          <p>ຍັງບໍ່ໄດ້ຕັ້ງ FACEBOOK_APP_ID / FACEBOOK_APP_SECRET ໃນ server</p>
        ) : null}
        {setup.usesLocalhostRedirect ? (
          <p>
            Meta ມັກບລັອກ OAuth ກັບ <code className="text-xs">localhost</code> —
            ແນະນຳໃຊ້ ngrok ແລ້ວຕັ້ງ{" "}
            <code className="text-xs">PUBLIC_APP_URL</code> ແລະ{" "}
            <code className="text-xs">FACEBOOK_OAUTH_REDIRECT_URI</code> ເປັນ URL
            HTTPS ຂອງ tunnel (ຄ່າເດີມຕ້ອງກົງກັນທຸກຕົວ)
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs">OAuth Redirect URI:</span>
          <code className="break-all rounded bg-muted px-2 py-1 text-xs">
            {setup.oauthRedirectUri}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7"
            onClick={() => void copyRedirectUri()}
          >
            <Copy className="size-3" />
            ຄັດລອກ
          </Button>
        </div>
        {setup.hints.length > 0 ? (
          <ul className="list-disc space-y-1 ps-4 text-sm">
            {setup.hints.map((hint) => (
              <li key={hint}>{hint}</li>
            ))}
          </ul>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
